/**
 * Cookie consent manager (GDPR / ePrivacy + CCPA/CPRA).
 *
 * Same consent system as the portal app (younet_frontend); the consent
 * cookie is written with Domain=.younet.ai so a choice made on either the
 * landing site or the portal applies to both.
 *
 * - Stores the visitor's choices in a first-party cookie (strictly necessary,
 *   allowed without consent) with a localStorage mirror as fallback.
 * - Region-aware: EU/UK/unknown => opt-in (nothing non-essential runs until
 *   accepted); US => opt-out (defaults on until the visitor opts out).
 * - Honors Do Not Track / Global Privacy Control signals.
 * - Applies Google Consent Mode v2 updates and injects gtag.js, the Meta
 *   Pixel (fbevents.js) and the Awin affiliate script only once the relevant
 *   category is granted (their unconditional tags were removed from
 *   public/index.html; the gtag and fbq stubs there only queue).
 */

export const CONSENT_POLICY_VERSION = '2026-07-07';
export const CONSENT_COOKIE_NAME = 'younet_cookie_consent';
export const OPEN_COOKIE_SETTINGS_EVENT = 'younet:open-cookie-settings';
export const PRIVACY_POLICY_URL = '/terms-and-conditions#privacy-policy';

const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // re-ask after 12 months
const GTAG_SRC = 'https://www.googletagmanager.com/gtag/js?id=G-JB21XXPBW3';
const AWIN_SRC = 'https://www.dwin1.com/69230.js'; // Awin affiliate tracking (marketing)
// Meta Pixel 569201103273541 (marketing): the stub in public/index.html has
// queued init + PageView; loading the script sends them
const META_PIXEL_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

// EU/EEA/UK/CH — opt-in model. Everything unknown also falls back to opt-in.
const EU_TIMEZONE_PATTERNS = [/^Europe\//, /^Atlantic\/(Canary|Madeira|Azores|Reykjavik|Faroe)$/];
// US — CCPA/CPRA opt-out model.
const US_TIMEZONE_PATTERNS = [
    /^America\/(New_York|Detroit|Chicago|Menominee|Denver|Boise|Phoenix|Los_Angeles|Anchorage|Juneau|Sitka|Metlakatla|Yakutat|Nome|Adak)$/,
    /^America\/(Indiana|Kentucky|North_Dakota)\//,
    /^Pacific\/Honolulu$/,
    /^US\//,
];

const listeners = new Set();

/**
 * Best-effort region detection from the browser timezone. There is no geo
 * signal (CDN header / backend IP lookup) available to the frontend today,
 * so anything that is not confidently US gets the stricter opt-in model.
 * Returns 'eu' | 'us' | 'other'.
 */
export const detectRegion = () => {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        if (EU_TIMEZONE_PATTERNS.some((re) => re.test(tz))) return 'eu';
        if (US_TIMEZONE_PATTERNS.some((re) => re.test(tz))) return 'us';
    } catch {
    }
    return 'other';
};

/** 'optin' (EU + unknown) or 'optout' (US). */
export const getRegionModel = (region = detectRegion()) => (region === 'us' ? 'optout' : 'optin');

/**
 * Browser-level opt-out signals. The privacy policy promises Do Not Track is
 * honored, and Global Privacy Control is a legally binding opt-out under
 * CCPA/CPRA — so either signal suppresses the defaults-on (US) behavior.
 * An explicit in-banner choice made by the user still takes precedence.
 */
export const hasUniversalOptOutSignal = () => {
    try {
        return navigator.globalPrivacyControl === true
            || navigator.doNotTrack === '1'
            || window.doNotTrack === '1'
            || navigator.msDoNotTrack === '1';
    } catch {
        return false;
    }
};

const cookieDomain = () => {
    const host = window.location.hostname;
    return host === 'younet.ai' || host.endsWith('.younet.ai') ? '.younet.ai' : '';
};

const writeCookie = (name, value, maxAgeSeconds) => {
    const domain = cookieDomain();
    let cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax`;
    if (domain) cookie += `; Domain=${domain}`;
    if (window.location.protocol === 'https:') cookie += '; Secure';
    document.cookie = cookie;
};

const readCookie = (name) => {
    const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.slice(name.length + 1)) : '';
};

const deleteCookie = (name) => {
    const expired = 'Max-Age=0; Path=/';
    document.cookie = `${name}=; ${expired}`;
    const domain = cookieDomain();
    if (domain) document.cookie = `${name}=; ${expired}; Domain=${domain}`;
};

/**
 * The stored consent record, or null when the visitor has not decided yet
 * (or decided under an older policy version, which requires re-asking).
 * Shape: {version, timestamp, region, method, categories: {necessary, analytics, marketing}}
 */
export const getStoredConsent = () => {
    try {
        const raw = readCookie(CONSENT_COOKIE_NAME) || localStorage.getItem(CONSENT_COOKIE_NAME) || '';
        if (!raw) return null;
        const record = JSON.parse(raw);
        if (record?.version !== CONSENT_POLICY_VERSION || !record?.categories) return null;
        return record;
    } catch {
        return null;
    }
};

/**
 * Effective category state used by all tracking gates. Without a stored
 * record: EU/unknown visitors get everything non-essential denied (opt-in),
 * US visitors get defaults granted (opt-out model) unless a DNT/GPC signal
 * is present.
 */
export const getEffectiveConsent = () => {
    const stored = getStoredConsent();
    if (stored) return stored.categories;
    const defaultsOn = getRegionModel() === 'optout' && !hasUniversalOptOutSignal();
    return {necessary: true, analytics: defaultsOn, marketing: defaultsOn};
};

export const hasConsent = (category) => Boolean(getEffectiveConsent()[category]);

const notify = () => {
    const categories = getEffectiveConsent();
    listeners.forEach((fn) => {
        try {
            fn(categories);
        } catch {
        }
    });
};

/** Subscribe to consent changes; returns an unsubscribe function. */
export const onConsentChange = (fn) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
};

const updateGtagConsent = (categories) => {
    if (typeof window.gtag !== 'function') return;
    window.gtag('consent', 'update', {
        analytics_storage: categories.analytics ? 'granted' : 'denied',
        ad_storage: categories.marketing ? 'granted' : 'denied',
        ad_user_data: categories.marketing ? 'granted' : 'denied',
        ad_personalization: categories.marketing ? 'granted' : 'denied',
    });
};

const loadScriptOnce = (src) => {
    if (document.querySelector(`script[src="${src}"]`)) return;
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
};

// Best-effort cleanup of cookies gtag/Meta/Awin already set, for consent withdrawal.
const clearNonEssentialCookies = () => {
    document.cookie.split('; ').forEach((row) => {
        const name = row.split('=')[0];
        if (/^(_ga|_gid|_gat|_gcl|_gac|_fbp|_fbc|_aw|aw)/.test(name)) deleteCookie(name);
    });
};

/**
 * Push the current effective consent into Google Consent Mode and load the
 * consent-gated scripts for granted categories. Safe to call repeatedly.
 * Once loaded, a script cannot be unloaded without a reload, but a "denied"
 * consent-mode update stops gtag from writing/reading cookies.
 */
export const applyConsent = () => {
    const categories = getEffectiveConsent();
    updateGtagConsent(categories);
    if (categories.analytics || categories.marketing) {
        loadScriptOnce(GTAG_SRC);
    }
    if (categories.marketing) {
        loadScriptOnce(META_PIXEL_SRC);
        loadScriptOnce(AWIN_SRC);
    }
    if (!categories.analytics && !categories.marketing) {
        clearNonEssentialCookies();
    }
};

/**
 * Persist an explicit choice (proof of consent: choices + timestamp +
 * policy version + region + how it was given), then apply it.
 * method: 'accept_all' | 'reject_all' | 'preferences' | 'dismissed'
 */
export const saveConsent = ({analytics = false, marketing = false}, method = 'preferences') => {
    const record = {
        version: CONSENT_POLICY_VERSION,
        timestamp: new Date().toISOString(),
        region: detectRegion(),
        method,
        categories: {necessary: true, analytics: Boolean(analytics), marketing: Boolean(marketing)},
    };
    const serialized = JSON.stringify(record);
    writeCookie(CONSENT_COOKIE_NAME, serialized, CONSENT_MAX_AGE_SECONDS);
    try {
        localStorage.setItem(CONSENT_COOKIE_NAME, serialized);
    } catch {
    }
    applyConsent();
    notify();
    return record;
};

/** Reopen the preferences dialog from anywhere (footer links, help pages…). */
export const openCookieSettings = () => {
    window.dispatchEvent(new CustomEvent(OPEN_COOKIE_SETTINGS_EVENT));
};
